import './LineChart.scss';

import { memo, useMemo } from 'react';
import CoordinateSystem from 'components/CoordinateSystem/CoordinateSystem';
import Legend, { LegendItem } from 'components/Legend/Legend';
import { Tooltip } from '@trackman/web-shared-components';
import { displayDate } from 'utilities/dateHelpers';
import { calculateStepSize } from 'components/ScatterChart/ScatterChart';

export type DataPointVisibility = 'visible' | 'hidden' | 'hovered';
export type LegendVisibility = 'visible' | 'hidden';
export type DatasetDrawMode = 'groupColor' | 'gradient';
export type LineChartPoint = { x: string | number; y: number };
export type LineChartDataset = { label: string; points: LineChartPoint[] };
export type GraphicalLineChartDataset = LineChartDataset & {
  color: string;
  drawMode: DatasetDrawMode;
  getGradientStops?: (point: LineChartPoint, nextPoint: LineChartPoint) => { offset: number; color: string }[];
  strokeWidth?: number;
  legendItem?: LegendItem;
};

type Props = {
  xLabel: string;
  yLabel: string;
  chartData: GraphicalLineChartDataset[];
  xStepSize?: number;
  yStepSize?: number;
  dataPointVisibility?: DataPointVisibility;
  legendVisibility?: LegendVisibility;
};

const DataPoint = memo(({ x, y, color, tooltipTitle, visibility }: any) => (
  <Tooltip title={tooltipTitle} placement='bottom' arrow>
    <div className={`data-point-container ${visibility}`} style={{ left: `${x}%`, bottom: `${y}%`, pointerEvents: 'auto' }}>
      <div className='data-point' style={{ backgroundColor: color }}></div>
    </div>
  </Tooltip>
));

const LineChart = ({
  xLabel,
  yLabel,
  chartData,
  xStepSize,
  yStepSize,
  dataPointVisibility = 'visible',
  legendVisibility = 'visible',
}: Props) => {
  const processedData = useMemo(() => {
    const allPointsFlat: LineChartPoint[] = [];

    const datasets = chartData.map((group) => {
      const sorted = getSortedPoints(group.points);
      allPointsFlat.push(...sorted);
      return { ...group, sortedPoints: sorted };
    });

    const scales = getChartLines(allPointsFlat, xStepSize, yStepSize);

    const legendItems: LegendItem[] = datasets
      .filter((group) => group.sortedPoints.length)
      .map(
        (group) =>
          group.legendItem ?? {
            label: group.label,
            dotProps: { color: group.color },
          }
      );

    return { datasets, scales, legendItems };
  }, [chartData]);

  const { datasets, scales, legendItems } = processedData;
  const { xTicks, yTicks, xRelative, yRelative, getTooltip } = scales;

  return (
    <div className='LineChart'>
      <CoordinateSystem xLabel={xLabel} yLabel={yLabel} xTicks={xTicks} yTicks={yTicks}>
        <svg viewBox='0 0 100 100' preserveAspectRatio='none' style={{ overflow: 'visible' }}>
          <defs>
            {datasets.map((group) => {
              if (group.drawMode !== 'gradient') return null;

              return group.sortedPoints.slice(0, -1).map((point, i) => {
                const nextPoint = group.sortedPoints[i + 1];
                const gradientId = `grad-${group.label.replace(/\s/g, '')}-${i}`;
                const stops = group.getGradientStops?.(point, nextPoint) ?? [];

                return (
                  <linearGradient
                    key={gradientId}
                    id={gradientId}
                    x1={`${xRelative(point.x)}%`}
                    y1={`${100 - yRelative(point.y)}%`}
                    x2={`${xRelative(nextPoint.x)}%`}
                    y2={`${100 - yRelative(nextPoint.y)}%`}
                    gradientUnits='userSpaceOnUse'
                  >
                    {stops.map((stop, si) => (
                      <stop key={si} offset={`${stop.offset}%`} stopColor={stop.color} />
                    ))}
                  </linearGradient>
                );
              });
            })}
          </defs>

          {datasets.map((group) => {
            if (group.sortedPoints.length < 2) return null;

            if (group.drawMode === 'groupColor') {
              const d = group.sortedPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xRelative(p.x)} ${100 - yRelative(p.y)}`).join(' ');

              return (
                <path
                  key={group.label}
                  d={d}
                  fill='none'
                  stroke={group.color}
                  strokeWidth={group.strokeWidth ?? 2}
                  vectorEffect='non-scaling-stroke'
                />
              );
            }

            return group.sortedPoints
              .slice(0, -1)
              .map((point, i) => (
                <path
                  key={`${group.label}-${i}`}
                  d={`M ${xRelative(point.x)} ${100 - yRelative(point.y)} L ${xRelative(group.sortedPoints[i + 1].x)} ${100 - yRelative(group.sortedPoints[i + 1].y)}`}
                  fill='none'
                  stroke={`url(#grad-${group.label.replace(/\s/g, '')}-${i})`}
                  strokeWidth={group.strokeWidth ?? 2}
                  vectorEffect='non-scaling-stroke'
                />
              ));
          })}
        </svg>

        {dataPointVisibility !== 'hidden' && (
          <div className='points-layer' style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            {datasets.map((group) =>
              group.sortedPoints.map((point, i) => (
                <DataPoint
                  key={`${group.label}-${i}`}
                  point={point}
                  color={group.color}
                  x={xRelative(point.x)}
                  y={yRelative(point.y)}
                  tooltipTitle={getTooltip(point)}
                  visibility={dataPointVisibility}
                />
              ))
            )}
          </div>
        )}
      </CoordinateSystem>
      {legendVisibility !== 'hidden' && <Legend items={legendItems} />}
    </div>
  );
};

export default LineChart;

const getSortedPoints = (points: LineChartPoint[]) => {
  return [...points].sort((a, b) => {
    if (typeof a.x === 'string' && typeof b.x === 'string') {
      return new Date(a.x as string).getTime() - new Date(b.x as string).getTime();
    }
    return (a.x as number) - (b.x as number);
  });
};

const getChartLines = (allPoints: LineChartPoint[], xStepSize?: number, yStepSize?: number) => {
  if (allPoints.length === 0) {
    return {
      xTicks: [],
      yTicks: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      xRelative: (x: string | number) => 0,
      yRelative: (y: number) => y,
      getTooltip: (point: LineChartPoint) => `${point.x}: ${point.y}`,
    };
  }

  const yMin = Math.min(...allPoints.map((point) => point.y));
  const yMax = Math.max(...allPoints.map((point) => point.y));
  const yRange = yMax - yMin;

  const yStep = yStepSize ?? calculateStepSize(yRange);
  const yTicks = Array.from({ length: Math.ceil(yRange / yStep) + (yRange === 0 ? 4 : 2) }, (_, i) =>
    Number((Math.floor(yMin / yStep - (yRange === 0 ? 1 : 0)) * yStep + i * yStep).toFixed(1))
  );

  const yRelative = (value: number) => {
    if (yTicks[yTicks.length - 1] - yTicks[0] === 0) return 0;
    return ((value - yTicks[0]) / (yTicks[yTicks.length - 1] - yTicks[0])) * 100;
  };

  let xMin: number;
  let xMax: number;
  let xTicks: (string | number)[] = [];
  let xRelative: (value: string | number) => number;
  let getTooltip: (point: LineChartPoint) => string;

  if (typeof allPoints[0].x === 'string') {
    const xMinDate = new Date(Math.min(...allPoints.map((p) => new Date(p.x as string).getTime())));
    const xMaxDate = new Date(Math.max(...allPoints.map((p) => new Date(p.x as string).getTime())));
    xMin = xMinDate.getTime();
    xMax = xMaxDate.getTime();
    const timeRange = xMax - xMin;

    const uniqueDates = new Set(allPoints.map((p) => new Date(p.x as string).toISOString().slice(0, 10)));
    const numberOfTicks = Math.max(Math.min(uniqueDates.size, 8), 3);

    for (let i = 0; i < numberOfTicks; i++) {
      const tickTime = xMin + (timeRange / (numberOfTicks - 1)) * i;
      const date = new Date(tickTime);
      xTicks.push(`${formatTickDate(date)}`);
    }

    xRelative = (value: string | number) => {
      const valueTime = new Date(value as string).getTime();
      if (timeRange === 0) return 0;
      return ((valueTime - xMin) / timeRange) * 100;
    };

    getTooltip = (point: LineChartPoint) => `${displayDate(point.x)}: ${point.y.toFixed(2)}`;
  } else {
    xMin = Math.min(...allPoints.map((p) => p.x as number));
    xMax = Math.max(...allPoints.map((p) => p.x as number));
    const range = xMax - xMin;
    const xStep = xStepSize ?? calculateStepSize(range);
    const xMinTick = Math.floor(xMin / xStep) * xStep;
    const xSteps = Math.ceil((xMax - xMinTick) / xStep) + 1;
    xTicks = Array.from({ length: xSteps }, (_, i) => Number((xMinTick + i * xStep).toFixed(1)));
    const xTickRange = Number(xTicks[xTicks.length - 1]) - Number(xTicks[0]);

    xRelative = (value: string | number) => (xTickRange === 0 ? 0 : ((Number(value) - xMinTick) / xTickRange) * 100);
    getTooltip = (point: LineChartPoint) => `${point.x}: ${point.y.toFixed(2)}`;
  }

  return {
    xTicks,
    yTicks,
    xRelative,
    yRelative,
    getTooltip,
  };
};

const tickDateFormatter = new Intl.DateTimeFormat('en-US', {
  year: '2-digit',
  month: 'short',
  day: '2-digit',
});

const formatTickDate = (date: string | number | Date): string => {
  if (!date) {
    return '';
  }
  return tickDateFormatter.format(new Date(date));
};
